import { Button, Checkbox } from '@mui/material'
import { FC, useContext, useEffect, useState, useCallback } from 'react'

import { useTranslation } from 'react-i18next'
import closeIcon from '../../../../../assets/images/icons/x-close.svg'
import { UserDataContext } from '../../../../../providers/contexts/UserDataProvider'
import { handleb2b2cOptIn } from '../../../../../services/dashboard'
import { UserRole } from '../../../../../utils/constants'
import { OPT_IN_OPTIONS, ROUTES } from '../../../../../utils/constants/enum'
import CustomModal from '../../../../modals/custom-modal'
import styles from './opt-in.module.scss'

type OptInModalProps = {
    open: boolean
    closeModal: () => void
    variant?: UserRole
}

export const OptIn: FC<OptInModalProps> = ({ open, closeModal, variant }) => {
    const { t } = useTranslation()
    const { setUserData } = useContext(UserDataContext)

    const [isB2b2cChecked, setIsB2b2cChecked] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [errorState, setErrorState] = useState<{
        visible: boolean
        message: string
    }>({
        visible: false,
        message: '',
    })

    // Function to handle the opt-in process
    const handleOptIn = useCallback(async () => {
        setErrorState({ visible: false, message: '' })
        setIsLoading(true)

        const response = await handleb2b2cOptIn(OPT_IN_OPTIONS.OPT_IN)

        if (response?.messageCode === 'HF-1114') {
            setErrorState({ visible: true, message: response.message })
        } else if (response.success) {
            setUserData(response.data)
            closeModal()
        } else {
            setErrorState({ visible: true, message: response.message })
        }
        setIsLoading(false)
    }, [closeModal, setUserData])

    // Reset states when modal is opened or closed
    useEffect(() => {
        setIsB2b2cChecked(false)
        setErrorState({ visible: false, message: '' })
    }, [open])

    const renderContent = () => {
        if (variant === UserRole.Client) {
            return t('rewards_program_opt.opt_in_content_customer')
        }

        return (
            <>
                <p className={styles['opt-in-title']}>
                    {t('rewards_program_opt.b2b2c_opt_in_content_new_title')}
                </p>
                <ul>
                    {['one', 'two', 'three'].map((point) => (
                        <li key={point} className={styles['list-item-bullet']}>
                            <span className={styles.bold}>
                                {t(
                                    `rewards_program_opt.b2b2c_opt_in_content_new_point_${point}_title`
                                )}
                            </span>
                            <span>
                                {' '}
                                {t(
                                    `rewards_program_opt.b2b2c_opt_in_content_new_point_${point}_content`
                                )}
                            </span>
                        </li>
                    ))}
                </ul>
            </>
        )
    }

    return (
        <CustomModal open={open}>
            <div className={styles['header']}>
                <h1 className={styles['title']}>
                    {variant === UserRole.Client
                        ? t('rewards_program_opt.opt_in_heading')
                        : t('rewards_program_opt.b2b2c_opt_in_heading')}
                </h1>
                <img
                    className={styles['close']}
                    onClick={closeModal}
                    src={closeIcon}
                    alt="x"
                />
            </div>

            <div className={styles['main']}>
                <div className={styles['terms-container']}>
                    <div className={styles['terms-text']}>
                        {renderContent()}
                    </div>
                    <div className={styles['terms-checkbox-container']}>
                        <Checkbox
                            className={styles['terms-checkbox']}
                            checked={isB2b2cChecked}
                            id="signB2b2c"
                            onChange={() => setIsB2b2cChecked((prev) => !prev)}
                        />
                        <span className={styles['terms-checkbox-content']}>
                            {t('rewards_program_opt.agree_to_text')}{' '}
                            <a
                                href={`${ROUTES.TERMS_AND_CONDITIONS}en`}
                                target="_blank"
                                rel="noreferrer"
                                className={styles['link']}
                            >
                                {variant === UserRole.Client
                                    ? t('rewards_program_opt.terms_conditions')
                                    : t(
                                          'rewards_program_opt.my_beauty_health_terms_conditions'
                                      )}
                            </a>
                            .
                        </span>
                    </div>
                </div>

                <div className={styles['button-container']}>
                    <Button
                        variant="contained"
                        disabled={!isB2b2cChecked || isLoading}
                        onClick={handleOptIn}
                        className="secondary-btn"
                    >
                        {isLoading
                            ? t('rewards_program_opt.sign_up_loading')
                            : t('rewards_program_opt.sign_up')}
                    </Button>

                    {errorState.visible && (
                        <p className="error-txt">
                            {errorState.message ||
                                t('common_elements.something_wrong_text')}
                        </p>
                    )}
                </div>
            </div>
        </CustomModal>
    )
}
